class CompactMirrorDrawer extends HTMLElement {
  constructor() {
    super();
    this.onOpen = this.onOpen.bind(this);
    this.onKeyup = (event) => event.key === 'Escape' && this.close();
  }

  connectedCallback() {
    this.variants = JSON.parse(this.querySelector('[data-compact-mirror-variants]').textContent);
    this.groups = Array.from(this.querySelectorAll('[data-compact-mirror-group]'), (element) => ({
      element,
      key: element.dataset.compactMirrorGroup,
      label: element.querySelector('[data-compact-mirror-group-label]'),
      inputs: Array.from(element.querySelectorAll('input[type="radio"]')),
    }));
    this.productPanes = Array.from(this.querySelectorAll('[data-compact-mirror-product]'));
    this.priceEl = this.querySelector('[data-compact-mirror-price]');
    this.errorEl = this.querySelector('[data-compact-mirror-error]');
    this.addButton = this.querySelector('[data-compact-mirror-add]');
    this.cartButton = this.querySelector('[data-compact-mirror-cart-add]');

    this.querySelectorAll('[data-compact-mirror-close]').forEach((el) => el.addEventListener('click', () => this.close()));
    this.addButton.addEventListener('click', () => this.addToProduct());
    this.cartButton.addEventListener('click', () => this.addToCart());
    this.groups.forEach(({ key, inputs }) =>
      inputs.forEach((input) => input.addEventListener('change', () => this.onOptionChange(key, input.value))),
    );
    document.addEventListener('compact-mirror:open', this.onOpen);
  }

  disconnectedCallback() {
    document.removeEventListener('compact-mirror:open', this.onOpen);
    document.removeEventListener('keyup', this.onKeyup);
  }

  onOpen(event) {
    this.trigger = event.detail?.button;
    this.cartContext = event.detail?.cartContext || null;
    this.row = this.trigger?.closest('[data-compact-mirror-widget]');
    if (!this.row && !this.cartContext) return;

    this.idInput = this.row?.querySelector('[data-compact-mirror-id-input]');
    const variantId = Number(this.idInput?.value);
    this.selectVariant(
      this.variants.find((variant) => variant.id === variantId && variant.available) ||
        this.variants.find((variant) => variant.available),
    );
    this.setError('');
    this.addButton.querySelector('span').textContent = this.cartContext ? 'Add to bag' : 'Add to product';
    this.addButton.classList.toggle('hidden', !this.cartContext);
    this.cartButton.style.display = this.cartContext ? 'none' : '';

    if (window.innerWidth <= 750) this.previousScrollY = window.scrollY;
    const fromPdp = this.trigger.closest('[js-main-product]') && !this.trigger.closest('.quick-add-modal');
    document.body.classList.add('overflow-hidden');
    document.body.classList.toggle('pdp-drawer-open', Boolean(fromPdp));
    document.addEventListener('keyup', this.onKeyup);
    this.setAttribute('aria-hidden', 'false');
    this.classList.add('is-open');
  }

  close() {
    document.body.classList.remove('overflow-hidden', 'pdp-drawer-open');
    document.removeEventListener('keyup', this.onKeyup);
    this.setAttribute('aria-hidden', 'true');
    this.classList.remove('is-open');
    if (this.previousScrollY != null) {
      window.scrollTo({ top: this.previousScrollY, behavior: 'auto' });
      this.previousScrollY = null;
    }
  }

  variantMatches(variant, options, keys) {
    return keys.every((key) => variant.options[key] === undefined || variant.options[key] === options[key]);
  }

  onOptionChange(key, value) {
    const keys = this.groups.map((group) => group.key);
    const earlierKeys = keys.slice(0, keys.indexOf(key));
    const wanted = { ...this.variant.options, [key]: value };
    const candidates = this.variants.filter((variant) => variant.available && variant.options[key] === value);

    this.selectVariant(
      candidates.find((variant) => this.variantMatches(variant, wanted, keys)) ||
        candidates.find((variant) => this.variantMatches(variant, wanted, earlierKeys)) ||
        candidates[0],
    );
  }

  selectVariant(variant) {
    if (!variant) return;
    this.variant = variant;
    const { options } = variant;

    this.groups.forEach((group, index) => {
      const applies = options[group.key] !== undefined;
      group.element.hidden = !applies;
      if (!applies) return;

      group.label.textContent = options[group.key];
      const earlierKeys = this.groups
        .slice(0, index)
        .map(({ key }) => key)
        .filter((key) => options[key] !== undefined);

      group.inputs.forEach((input) => {
        const available = this.variants.some(
          (item) => item.available && item.options[group.key] === input.value && this.variantMatches(item, options, earlierKeys),
        );
        input.checked = input.value === options[group.key];
        input.disabled = !available;
        input.classList.toggle('disabled', !available);
        input.nextElementSibling.classList.toggle('is-active', input.checked);
      });
    });

    this.productPanes.forEach((pane) => {
      pane.hidden = Number(pane.dataset.compactMirrorProduct) !== variant.product;
    });
    this.priceEl.textContent = variant.price_formatted;
    this.updateGallery();
  }

  updateGallery() {
    const gallery = this.querySelector('[data-compact-mirror-gallery]:not([hidden])');
    const slides = Array.from(gallery?.querySelectorAll('.product__media-item') || []);
    if (!slides.length) return;

    const normalize = (value) => (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const color = normalize(this.variant.options.color);
    const colorSlides = color ? slides.filter((slide) => normalize(slide.querySelector('img')?.alt).includes(color)) : [];
    const visibleSlides = colorSlides.length ? colorSlides : slides;
    const target =
      visibleSlides.find((slide) => this.variant.media_id && slide.dataset.mediaId?.endsWith(`-${this.variant.media_id}`)) ||
      visibleSlides[0];

    slides.forEach((slide) => {
      slide.classList.toggle('is-hidden', !visibleSlides.includes(slide));
      slide.classList.toggle('is-active', slide === target);
    });

    const slider = gallery.querySelector('slider-component');
    slider?.resetPages?.();
    slider?.querySelector('[id^="Slider-"]')?.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
  }

  getVariantLabel() {
    return this.groups
      .map(({ key }) => this.variant.options[key])
      .filter(Boolean)
      .join(' / ');
  }

  addToProduct() {
    if (this.cartContext) return this.addToBag();

    this.idInput.value = this.variant.id;
    this.trigger.dataset.price = (this.variant.price / 100).toFixed(2);
    const variantLabel = this.row.querySelector('[data-compact-mirror-variant]');
    if (variantLabel) variantLabel.textContent = this.getVariantLabel();

    this.row.closest('gift-widget')?.addGiftProduct(this.trigger);
    this.close();
  }

  addToBag() {
    const body = JSON.stringify({ items: [{ id: this.variant.id, quantity: 1 }] });
    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body })
      .then((response) => response.json())
      .then((response) => response.status && console.error(response))
      .catch((error) => console.error(error))
      .finally(() => document.dispatchEvent(new CustomEvent('compact-mirror:added')));
    this.close();
  }

  addToCart() {
    const formId = this.idInput.getAttribute('form');
    const form = document.getElementById(formId);
    const submitButton = form?.closest('product-form')?.querySelector('[type="submit"]');
    if (!form || !submitButton) return this.setError('Unable to find the product form.');
    if (submitButton.getAttribute('aria-disabled') === 'true') return this.setError('This product is currently unavailable.');

    const onResult = (event) => {
      if (event.detail?.productFormId !== formId) return;
      document.removeEventListener('product-form:submit-success', onResult);
      document.removeEventListener('product-form:submit-error', onResult);
      this.setLoading(false);
      if (event.type === 'product-form:submit-success') {
        this.close();
      } else {
        this.setError(event.detail.response?.description || 'Unable to add this product to the cart.');
      }
    };
    document.addEventListener('product-form:submit-success', onResult);
    document.addEventListener('product-form:submit-error', onResult);
    this.setLoading(true);

    const wasSelected = !this.idInput.disabled;
    this.idInput.value = this.variant.id;
    this.idInput.disabled = false;
    form.requestSubmit();
    this.idInput.disabled = !wasSelected;
  }

  setLoading(isLoading) {
    this.cartButton.classList.toggle('loading', isLoading);
    this.cartButton.disabled = isLoading;
    this.cartButton.querySelector('.loading__spinner')?.classList.toggle('hidden', !isLoading);
  }

  setError(message) {
    this.errorEl.textContent = message;
  }
}

if (!customElements.get('compact-mirror-drawer')) {
  customElements.define('compact-mirror-drawer', CompactMirrorDrawer);
}
